import { useMemo } from "react";
import { _Product_liquid, _Variant_liquid } from "./_types";
import { SingleSelectedConfigurableProduct } from "./product";
import { ProductGroupAccordion } from "./product-group-accordion";
import { ProductGroupVariantSelectorImages } from "./product-group-variant-selector-images";
import { ProductGroupCheckboxCard } from "./product-group-checkbox-card";

const DEFAULT_TITLE_LABEL = "Please select one option";
const DRILLING_OPTION_LINE_ITEM_TOKEN = "drilling";

interface Props {
  stepNumber: number;
  totalSteps: number;
  productList: _Product_liquid[];
  title?: string;
  collapsed?: boolean;
  filteringVariantIds?: number[];
  selectedProduct?: SingleSelectedConfigurableProduct;
  quantityToAdd?: number;
  displayPrices?: boolean;
  selectorTemplate?: "images" | "checkboxes";
  gridColumnsDesktop?: string;
  gridColumnsMobile?: string;
  required?: boolean;
  renderingStrategy?: "per_product" | "per_variant";
  openInfoInNewTab?: boolean;
  onProductSelect?: (
    stepNumber: number,
    product: _Product_liquid,
    variant: _Variant_liquid,
    quantityToAdd: number
  ) => void;
  onProductUnselect?: (stepNumber: number) => void;
}

export const ProductGroupStepSelector = ({
  stepNumber,
  totalSteps,
  title,
  filteringVariantIds,
  productList,
  selectedProduct,
  collapsed,
  displayPrices,
  quantityToAdd,
  selectorTemplate = "checkboxes",
  gridColumnsDesktop,
  gridColumnsMobile,
  required = true,
  renderingStrategy,
  openInfoInNewTab,
  onProductSelect,
  onProductUnselect,
}: Props) => {
  const productsToShow = useMemo(() => {
    if (typeof filteringVariantIds === "undefined") {
      return productList;
    }

    return productList
      .map((p) => {
        const variants = p.variants.filter((v) => {
          if (typeof filteringVariantIds !== "undefined" && !filteringVariantIds.includes(v.id)) {
            return false;
          }
          return true;
        });

        return {
          ...p,
          variants,
        };
      })
      .filter((p) => {
        return p.variants.length > 0;
      });
  }, [productList, filteringVariantIds]);

  // Hide step if no products available
  if (productsToShow && productsToShow.length === 0) {
    return null;
  }

  // Customize the title based on whether the step is required or optional
  const stepTitle = title || (required ? DEFAULT_TITLE_LABEL : "Optional - Select if needed");

  const shouldShowProductTitle = useMemo(() => {
    return !stepTitle.toLowerCase().includes(DRILLING_OPTION_LINE_ITEM_TOKEN);
  }, [stepTitle]);

  return (
    <ProductGroupAccordion
      collapsed={collapsed}
      index={stepNumber}
      title={stepTitle}
      rightInfo={
        <p className={"text-sm"}>
          ({stepNumber}/{totalSteps})
        </p>
      }
    >
      {selectorTemplate === "checkboxes" && (
        <ProductGroupCheckboxCard
          productList={productsToShow}
          gridColumnsDesktop={gridColumnsDesktop}
          gridColumnsMobile={gridColumnsMobile}
          selectedProduct={selectedProduct}
          selectedVariant={selectedProduct?.selectedVariant}
          displayPrice={displayPrices}
          renderingStrategy={renderingStrategy}
          shouldShowProductTitle={shouldShowProductTitle}
          quantityToAdd={quantityToAdd}
          stepNumber={stepNumber}
          openInfoInNewTab={openInfoInNewTab}
          onProductSelect={onProductSelect}
          onProductUnselect={onProductUnselect}
        />
      )}

      {selectorTemplate === "images" && (
        <ProductGroupVariantSelectorImages
          productList={productsToShow}
          selectedProduct={selectedProduct?.product}
          displayPrice={displayPrices}
          selectedVariant={selectedProduct?.selectedVariant}
          gridColumnsDesktop={gridColumnsDesktop}
          gridColumnsMobile={gridColumnsMobile}
          required={required}
          renderingStrategy={renderingStrategy}
          shouldShowProductTitle={shouldShowProductTitle}
          onProductSelect={(selectedProduct, selectedVariant, isChecked) => {
            if (!isChecked) {
              onProductSelect &&
                onProductSelect(
                  stepNumber,
                  selectedProduct,
                  selectedVariant,
                  Number(quantityToAdd) || 1
                );
            } else {
              onProductUnselect && onProductUnselect(stepNumber);
            }
          }}
        />
      )}
    </ProductGroupAccordion>
  );
};
